/**
 * Chatbot composable - manages conversation state and Firestore persistence.
 * Uses server-side Vertex AI API for chat responses.
 */
import { ref, computed, watch } from 'vue'
import { collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp, Timestamp, getDocs, deleteDoc } from 'firebase/firestore'
import { useCurrentUser, useFirestore } from 'vuefire'

export interface ChatMessage {
  id?: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

interface FirestoreChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: Timestamp | ReturnType<typeof serverTimestamp>
}

// Singleton state for the chatbot
const messages = ref<ChatMessage[]>([])
const isLoading = ref(false)
const loadingStatus = ref<string>('')
const isOpen = ref(false)
const error = ref<string | null>(null)

// Themed loading messages that cycle while waiting
const LOADING_MESSAGES = [
  'Consulting the infinite improbability drive...',
  'Scanning the galaxy for answers...',
  'Channeling intergalactic wisdom...',
  'Querying the Chewbacchus archives...',
  'Beaming up relevant data...',
  'Warping through the knowledge base...',
  'Decrypting ancient krewe secrets...',
  'Summoning the spirit of Bacchus...',
  'Navigating the parade route of knowledge...',
  'Tuning into the cosmic frequencies...',
]

// Messages for when fetching from web
const WEB_FETCH_MESSAGES = [
  'Scouting chewbacchus.org for intel...',
  'Hacking into the mothership (chewbacchus.org)...',
  'Downloading fresh krewe data...',
  'Intercepting transmissions from HQ...',
  'Probing the chewbacchus.org mainframe...',
]

let loadingInterval: ReturnType<typeof setInterval> | null = null
let loadingStartTime = 0

let unsubscribeHistory: (() => void) | null = null

/**
 * Start cycling through loading messages
 */
function startLoadingMessages() {
  loadingStartTime = Date.now()
  loadingStatus.value = LOADING_MESSAGES[0] ?? ''
  let messageIndex = 0
  let useWebMessages = false

  loadingInterval = setInterval(() => {
    const elapsed = Date.now() - loadingStartTime

    // After 3 seconds, switch to web fetch messages (likely fetching from site)
    if (elapsed > 3000 && !useWebMessages) {
      useWebMessages = true
      messageIndex = 0
    }

    const messages = useWebMessages ? WEB_FETCH_MESSAGES : LOADING_MESSAGES
    messageIndex = (messageIndex + 1) % messages.length
    loadingStatus.value = messages[messageIndex] ?? ''
  }, 2000)
}

/**
 * Stop cycling loading messages
 */
function stopLoadingMessages() {
  if (loadingInterval) {
    clearInterval(loadingInterval)
    loadingInterval = null
  }
  loadingStatus.value = ''
}

export function useChatbot() {
  const user = useCurrentUser()
  const db = useFirestore()

  // Computed refs
  const hasMessages = computed(() => messages.value.length > 0)
  const userId = computed(() => user.value?.uid || null)

  /**
   * Subscribe to chat history from Firestore
   */
  function subscribeToHistory() {
    if (!userId.value || unsubscribeHistory) return

    const historyRef = collection(db, 'members', userId.value, 'chatHistory')
    const historyQuery = query(historyRef, orderBy('timestamp', 'asc'), limit(50))

    unsubscribeHistory = onSnapshot(historyQuery, (snapshot) => {
      const loadedMessages: ChatMessage[] = []
      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as FirestoreChatMessage
        loadedMessages.push({
          id: docSnap.id,
          role: data.role,
          content: data.content,
          timestamp: data.timestamp instanceof Timestamp
            ? data.timestamp.toDate()
            : new Date(),
        })
      })
      messages.value = loadedMessages
    }, (err) => {
      console.error('[useChatbot] History subscription error:', err)
    })
  }

  /**
   * Save a message to Firestore
   */
  async function saveMessage(role: 'user' | 'assistant', content: string) {
    if (!userId.value) return

    try {
      const historyRef = collection(db, 'members', userId.value, 'chatHistory')
      await addDoc(historyRef, {
        role,
        content,
        timestamp: serverTimestamp(),
      } satisfies FirestoreChatMessage)
    }
    catch (e) {
      console.error('[useChatbot] Failed to save message:', e)
    }
  }

  /**
   * Build history for API call (last N messages for context)
   */
  function buildHistoryForAPI(maxMessages = 10): Array<{ role: 'user' | 'assistant', content: string }> {
    const recentMessages = messages.value.slice(-maxMessages)
    return recentMessages.map(m => ({
      role: m.role,
      content: m.content,
    }))
  }

  /**
   * Send a message to the chatbot
   */
  async function sendMessage(text: string) {
    if (!text.trim()) return
    if (isLoading.value) return

    isLoading.value = true
    error.value = null
    startLoadingMessages()

    // Add user message immediately for UI
    const userMessage: ChatMessage = {
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    }
    messages.value.push(userMessage)

    // Save user message to Firestore
    await saveMessage('user', text.trim())

    try {
      // Build history for context (excluding the message we just added)
      const history = buildHistoryForAPI(10).slice(0, -1)

      // Call server API
      const response = await $fetch('/api/chatbot/message', {
        method: 'POST',
        body: {
          message: text.trim(),
          history,
          memberId: userId.value || undefined,
        },
      })

      const responseText = response.message || 'I apologize, but I couldn\'t generate a response. Please try again.'

      // Add assistant message
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: responseText,
        timestamp: new Date(),
      }
      messages.value.push(assistantMessage)

      // Save assistant message to Firestore
      await saveMessage('assistant', responseText)
    }
    catch (e) {
      console.error('[useChatbot] Failed to send message:', e)
      // The server says why for limits and access (too long, too many
      // questions, not signed up); anything else is a plain failure
      const status = (e as { statusCode?: number })?.statusCode
      const serverMessage = (e as { data?: { message?: string } })?.data?.message
      error.value = status && [400, 401, 403, 429].includes(status) && serverMessage
        ? serverMessage
        : 'Failed to get response. Please try again.'

      // Remove the user message if we failed
      messages.value.pop()
    }
    finally {
      stopLoadingMessages()
      isLoading.value = false
    }
  }

  /**
   * Reset local state only (used when switching users)
   */
  function resetLocalState() {
    if (unsubscribeHistory) {
      unsubscribeHistory()
      unsubscribeHistory = null
    }
    messages.value = []
    error.value = null
  }

  /**
   * Clear chat history - deletes from Firestore (explicit user action)
   */
  async function clearChat() {
    if (!userId.value) {
      messages.value = []
      error.value = null
      return
    }

    // Unsubscribe from history to prevent reloading during deletion
    if (unsubscribeHistory) {
      unsubscribeHistory()
      unsubscribeHistory = null
    }

    // Clear local state immediately for responsive UI
    messages.value = []
    error.value = null

    // Delete all chat history documents from Firestore
    try {
      const historyRef = collection(db, 'members', userId.value, 'chatHistory')
      const snapshot = await getDocs(historyRef)
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref))
      await Promise.all(deletePromises)
      console.warn(`[useChatbot] Deleted ${snapshot.docs.length} chat messages`)
    }
    catch (err) {
      console.error('[useChatbot] Error deleting chat history:', err)
    }

    // Resubscribe (will be empty now)
    subscribeToHistory()
  }

  /**
   * Toggle chat widget open/closed
   */
  function toggleChat() {
    isOpen.value = !isOpen.value
  }

  /**
   * Open the chat widget
   */
  function openChat() {
    isOpen.value = true
  }

  /**
   * Close the chat widget
   */
  function closeChat() {
    isOpen.value = false
  }

  // Watch for user changes to resubscribe to history
  watch(userId, (newId, oldId) => {
    if (newId !== oldId) {
      // Reset local state (don't delete from Firestore - just switching users)
      resetLocalState()

      // Subscribe to new user's history
      if (newId) {
        subscribeToHistory()
      }
    }
  }, { immediate: true })

  return {
    // State
    messages: computed(() => messages.value),
    isLoading: computed(() => isLoading.value),
    loadingStatus: computed(() => loadingStatus.value),
    isOpen: computed(() => isOpen.value),
    error: computed(() => error.value),
    hasMessages,

    // Actions
    sendMessage,
    clearChat,
    toggleChat,
    openChat,
    closeChat,
    subscribeToHistory,
  }
}
